import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Presupuesto } from "@/api/entities";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FileText, User, History, Plus } from "lucide-react";
import PresupuestoGenerator from "../components/lead-detail/PresupuestoGenerator";

/**
 * Página para crear un presupuesto independiente, sin lead asociado.
 * Los datos del cliente se introducen manualmente y se persisten en
 * la colección `presupuestos` con `lead_id: null` y `tipo: "independiente"`.
 *
 * Reutiliza PresupuestoGenerator: el cliente del formulario se pasa
 * como prop `lead` (mismo shape: nombre, empresa, email, telefono),
 * con `leadId={null}` para activar el modo independiente del generador.
 *
 * Listado superior: muestra los últimos presupuestos independientes
 * guardados; pulsando "Cargar" se rellena el formulario y el generador
 * vía la prop `presupuestoParaCargar`.
 */
const CLIENTE_VACIO = { nombre: "", empresa: "", email: "", telefono: "" };

export default function PresupuestoLibre() {
  const [cliente, setCliente] = useState(CLIENTE_VACIO);
  const [presupuestoParaCargar, setPresupuestoParaCargar] = useState(null);
  const [resetKey, setResetKey] = useState(0); // fuerza remount del generador al "Nuevo"

  const { data: independientes = [] } = useQuery({
    queryKey: ["presupuestos", "independientes"],
    queryFn: () => Presupuesto.list(
      { tipo: "independiente" },
      { orderBy: "created_date", orderDirection: "desc" }
    ),
  });

  const handleCambio = (campo, valor) => {
    setCliente(prev => ({ ...prev, [campo]: valor }));
  };

  const handleCargar = (p) => {
    setCliente({
      nombre: p.cliente?.nombre || "",
      empresa: p.cliente?.empresa || "",
      email: p.cliente?.email || "",
      telefono: p.cliente?.telefono || "",
    });
    setPresupuestoParaCargar(p);
    setResetKey(k => k + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNuevo = () => {
    setCliente(CLIENTE_VACIO);
    setPresupuestoParaCargar(null);
    setResetKey(k => k + 1);
  };

  const fmtFecha = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" });
  };
  const fmtEuro = (n) => (parseFloat(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-sm">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Presupuesto libre</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Crea un presupuesto sin asociarlo a un lead. Rellena los datos del cliente, genera el presupuesto y guárdalo o imprímelo.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={handleNuevo} className="shrink-0">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo
        </Button>
      </div>

      {independientes.length > 0 && (
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base dark:text-white">
              <History className="w-4 h-4" />
              Presupuestos guardados
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">({independientes.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                    <th className="py-2 pr-3">Nº</th>
                    <th className="py-2 pr-3">Cliente</th>
                    <th className="py-2 pr-3">Empresa</th>
                    <th className="py-2 pr-3">Fecha</th>
                    <th className="py-2 pr-3 text-right">Total</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {independientes.slice(0, 20).map(p => {
                    const activo = presupuestoParaCargar?.id === p.id;
                    return (
                      <tr
                        key={p.id}
                        className={`border-b border-gray-100 dark:border-gray-800 ${activo ? "bg-indigo-50 dark:bg-indigo-950" : ""}`}
                      >
                        <td className="py-2 pr-3 font-mono text-xs text-gray-500 dark:text-gray-400">{p.numero_presupuesto || "—"}</td>
                        <td className="py-2 pr-3 text-gray-900 dark:text-white">{p.cliente?.nombre || "—"}</td>
                        <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{p.cliente?.empresa || "—"}</td>
                        <td className="py-2 pr-3 text-gray-500 dark:text-gray-400">{fmtFecha(p.created_date)}</td>
                        <td className="py-2 pr-3 text-right font-medium text-gray-900 dark:text-white">{fmtEuro(p.total_presupuesto)} €</td>
                        <td className="py-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => handleCargar(p)} disabled={activo}>
                            {activo ? "Cargado" : "Cargar"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="dark:bg-gray-900 dark:border-gray-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base dark:text-white">
            <User className="w-4 h-4" />
            Datos del cliente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cliente-nombre" className="dark:text-gray-300">Nombre</Label>
              <Input
                id="cliente-nombre"
                value={cliente.nombre}
                onChange={e => handleCambio("nombre", e.target.value)}
                placeholder="Nombre y apellidos"
                className="dark:bg-gray-900 dark:border-gray-700"
              />
            </div>
            <div>
              <Label htmlFor="cliente-empresa" className="dark:text-gray-300">Empresa</Label>
              <Input
                id="cliente-empresa"
                value={cliente.empresa}
                onChange={e => handleCambio("empresa", e.target.value)}
                placeholder="Nombre de la empresa (opcional)"
                className="dark:bg-gray-900 dark:border-gray-700"
              />
            </div>
            <div>
              <Label htmlFor="cliente-email" className="dark:text-gray-300">Email</Label>
              <Input
                id="cliente-email"
                type="email"
                value={cliente.email}
                onChange={e => handleCambio("email", e.target.value)}
                placeholder="cliente@email.com"
                className="dark:bg-gray-900 dark:border-gray-700"
              />
            </div>
            <div>
              <Label htmlFor="cliente-telefono" className="dark:text-gray-300">Teléfono</Label>
              <Input
                id="cliente-telefono"
                value={cliente.telefono}
                onChange={e => handleCambio("telefono", e.target.value)}
                placeholder="600 000 000"
                className="dark:bg-gray-900 dark:border-gray-700"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <PresupuestoGenerator
        key={resetKey}
        leadId={null}
        lead={cliente}
        presupuestoParaCargar={presupuestoParaCargar}
      />
    </div>
  );
}
